import { Component, type ReactNode } from 'react';
import { Alert, Button } from '@heroui/react';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
  fallback?: (opts: { error: Error; reset: () => void }) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render errors in the subtree and shows a friendly recovery screen.
 * Data-fetch errors are handled separately by React Query; this is the
 * last-resort net for unexpected render-time failures.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }): void {
    // Surface unexpected render errors in the console for debugging.
    console.error('[ErrorBoundary]', error, info.componentStack);
    // Phase 25.5b-2 — forward to Sentry when configured. `captureException`
    // is a no-op (just stores locally, no network) when Sentry isn't
    // initialized, so this stays safe in dev.
    Sentry.captureException(error, {
      contexts: info.componentStack ? { react: { componentStack: info.componentStack } } : undefined,
    });
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback({ error: this.state.error, reset: this.reset });
      }
      return (
        <div className="p-6 max-w-xl mx-auto space-y-4">
          <Alert status="danger">
            <strong>Something went wrong.</strong>{' '}
            {this.state.error.message || 'An unexpected error occurred.'}
          </Alert>
          <div className="flex gap-2">
            <Button onPress={this.reset}>Try again</Button>
            <Button variant="ghost" onPress={() => window.location.reload()}>
              Reload page
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
